/**
 * V2 Yoink — rendering and small derivations. Split out of yoink.js on
 * 2026-07-25 (452 lines, plan budget ~400).
 *
 * Like the place view, renderYoink already took every input as a parameter
 * rather than closing over mount's state, so the render layer lifted out
 * unchanged. yoink.js re-exports socialLinkLabel, which the tests import.
 */
import * as gateway from '../../core/gateway.js';
import { imageSrc, isGooglePhoto } from '../../core/photo.js';
import { escapeHtml, sanitizeHtml } from '../../../assets/js/utils.js';
import { icon, categoryIcon } from '../../ui/icons.js';
import { shareButton } from '../../ui/share.js';
import { shareMenu } from '../../ui/share-menu.js';
import { slugify } from '../../../assets/js/shared/slugify.js';
import { canManage } from './manage.js';
import { bravoBarHtml, bravoEarnedHtml } from '../bravo/bravo.js';

/**
 * V1's social-link platform detection (yoink-panel.js:57-203), labels
 * intact; the branded SVG/color chrome flattens into the V2 ink skin.
 * Exported for tests.
 */
export function socialLinkLabel(url) {
  let host = '';
  try {
    host = new URL(url).hostname.replace(/^www\./, '').toLowerCase();
  } catch {
    return null;
  }
  const PLATFORMS = [
    ['instagram.com', 'View on Instagram'],
    ['tiktok.com', 'Watch on TikTok'],
    ['youtube.com', 'Watch on YouTube'],
    ['youtu.be', 'Watch on YouTube'],
    ['twitter.com', 'View on X'],
    ['x.com', 'View on X'],
    ['facebook.com', 'View on Facebook'],
    ['substack.com', 'Read on Substack'],
    ['medium.com', 'Read on Medium'],
    ['pinterest.com', 'View on Pinterest'],
    ['linkedin.com', 'View on LinkedIn'],
    ['threads.net', 'View on Threads'],
  ];
  const hit = PLATFORMS.find(([domain]) => host === domain || host.endsWith(`.${domain}`));
  return hit ? hit[1] : `View on ${host}`;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function stampDate(isoDate) {
  const d = new Date(isoDate);
  if (Number.isNaN(d.getTime())) return '';
  return `${MONTHS[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
}

export function imageUrls(yoink) {
  const images = Array.isArray(yoink.images) ? yoink.images : [];
  return images.filter(Boolean).map(imageSrc);
}

export function manageRow(store, yoink, confirming, deleting = false) {
  if (store.get().isReadOnly) return '';
  if (!canManage(store, yoink)) return '';
  if (confirming) {
    return `
      <div class="yoink-manage" role="group" aria-label="Manage this yoink">
        <span class="manage-confirm-text">Delete this yoink for good? This can’t be undone.</span>
        <button type="button" class="v2-btn-danger" data-yoink-delete-confirm ${deleting ? 'disabled' : ''}>
          ${deleting ? 'Deleting…' : 'Delete'}</button>
        <button type="button" class="log-change-place" data-yoink-delete-cancel ${deleting ? 'disabled' : ''}>Cancel</button>
      </div>`;
  }
  return `
    <div class="yoink-manage" role="group" aria-label="Manage this yoink">
      <button type="button" class="log-change-place" data-yoink-edit>Edit</button>
      <button type="button" class="log-change-place" data-yoink-delete>Delete</button>
      <span class="v2-form-error" data-manage-error role="alert"></span>
    </div>`;
}

/**
 * V1's carousel (yoink-panel.js:316-338): ONE photo at a time with
 * arrows, dots, a "1 / N" counter, swipe, and wrap-around — not a
 * vertical pile. Tapping the photo opens the fullscreen viewer
 * (M8.5 Tier 1).
 */
export function carouselHtml(urls, index, title, category) {
  if (!urls.length) {
    return `<div class="yoink-hero polaroid polaroid-blank">${categoryIcon(category, 64)}</div>`;
  }
  const many = urls.length > 1;
  return `
    <div class="yoink-hero polaroid yoink-carousel" data-photo-stage>
      <img src="${urls[index]}" alt="${escapeHtml(title || 'Yoink photo')}" data-photo-open
        title="Tap to view fullscreen">
      ${isGooglePhoto(urls[index])
        ? '<span class="photo-source">Photo via Google — add your own with Edit</span>' : ''}
      ${many ? `
      <button type="button" class="carousel-arrow carousel-prev" data-carousel-step="-1" aria-label="Previous photo">‹</button>
      <button type="button" class="carousel-arrow carousel-next" data-carousel-step="1" aria-label="Next photo">›</button>
      <span class="carousel-counter" aria-live="polite">${index + 1} / ${urls.length}</span>
      <span class="carousel-dots">
        ${urls.map((_, i) => `<button type="button" class="carousel-dot${i === index ? ' dot-active' : ''}"
          data-carousel-dot="${i}" aria-label="Photo ${i + 1} of ${urls.length}"></button>`).join('')}
      </span>` : ''}
    </div>`;
}

/**
 * The post-create share moment (Tier A auto-share, 2026-07-15): right
 * after logging, both share intents in one banner — the leather
 * "Share photos" (cards + caption, the make-a-post flow) leads, with
 * "Share link" beside it for texting. Flavor copy stays off the
 * buttons (familiarity rules).
 */
export function createdBannerHtml(shareState, title, bravoBalance = null) {
  const busy = shareState === 'building';
  const label = busy ? 'Making your card…'
    : shareState === 'copied' ? 'Link copied ✓'
    : shareState === 'failed' ? 'Ready — tap again'
    : 'Share photos';
  return `
    <div class="yoink-created" role="status">
      <span class="yoink-created-line">Yoinked! Show it off —</span>
      <button type="button" class="v2-btn-leather" data-share-card ${busy ? 'disabled' : ''}>${label}</button>
      ${shareButton(title)}
      <button type="button" class="log-change-place" data-share-card-skip>Not now</button>
      ${bravoEarnedHtml(bravoBalance)}
    </div>`;
}

export function renderYoink(el, yoink, store, confirming = false, photoIndex = 0, deleting = false, bannerHtml = '', collectionCtx = null, bravoState = null) {
  const urls = imageUrls(yoink);
  const who = yoink.public_profiles;
  const whoName = who?.full_name || who?.username || 'Anonymous';
  const place = yoink.locations;
  const category = yoink.categories;
  const note = yoink.description ? sanitizeHtml(yoink.description) : '';
  const basePath = window.YOINK_CONFIG?.basePath || '/adventure';

  // "Stop #31 in Dru's Airports — see all 47": the door from one texted
  // yoink to everything else the owner has logged in that category. The
  // category name stands alone — "collection" is reserved for the finite
  // is_set ones (National Parks, MLB Parks), and a yoink's category is a
  // tag. Only worth showing once there are two or more to see, and only
  // when we can actually build the filtered-journal link it promises.
  const own = store.get().user?.id === who?.id;
  const showCollection = collectionCtx && collectionCtx.count > 1 && who?.username && category?.name;
  const collectionStrip = showCollection ? `
        <button type="button" class="yoink-collection-strip" data-collection-journal>
          ${categoryIcon(category, 18)}
          <span class="collection-strip-text">
            ${collectionCtx.position ? `Stop #${collectionCtx.position} in` : 'Part of'}
            ${own ? 'your' : `${escapeHtml(whoName)}’s`}
            <strong>${escapeHtml(category.name)}</strong></span>
          <span class="collection-strip-count">see all ${collectionCtx.count}${collectionCtx.capped ? '+' : ''} →</span>
        </button>` : '';

  // You cannot bravo your own yoink (the server enforces it too), and a
  // signed-out or read-only visitor sees the count alone.
  const bravoBar = bravoBarHtml({
    count: yoink.bravo_count || 0,
    mine: bravoState?.mine === true,
    balance: bravoState?.balance ?? null,
    canGive: Boolean(store.get().user) && !store.get().isReadOnly && !own,
    pending: bravoState?.pending === true,
  });

  el.innerHTML = `
    <nav class="v2-back" aria-label="Back">
      <a href="${basePath}/" data-route="discover">← Discover</a>
      <span class="yoink-share-row">
        ${shareMenu(
          `<button type="button" class="v2-btn-share-photos" data-share-photos>
            ${icon('camera', 16)}<span data-share-photos-label>Share photos</span>
          </button>`,
          shareButton(yoink.title || 'A yoink')
        )}
      </span>
    </nav>
    ${bannerHtml}
    <article class="v2-place v2-yoink">
      ${carouselHtml(urls, photoIndex, yoink.title, category)}
      <div class="place-body">
        <div class="card-stamp">Yoinked<small>${stampDate(yoink.created_at)}</small></div>
        <h2 class="place-title">${escapeHtml(yoink.title || 'Untitled')}</h2>
        ${note ? `<div class="yoink-note">${note}</div>` : ''}
        <div class="card-meta yoink-meta">
          ${who?.username ? `
          <div class="meta-row meta-link" data-username="${escapeHtml(who.username)}" role="link" tabindex="0"
            aria-label="View ${escapeHtml(whoName)}’s journal">
            ${who.avatar_url
              ? `<img class="yoink-author-avatar" src="${gateway.getPublicUrl('avatars', who.avatar_url)}" alt="">`
              : icon('person')}<span>${escapeHtml(whoName)}</span>
          </div>` : ''}
          ${place?.id ? `
          <div class="meta-row meta-link" data-location-id="${place.id}" role="link" tabindex="0"
            aria-label="View location ${escapeHtml(place.display_name || '')}">
            ${icon('pin')}<span>${escapeHtml(place.display_name || 'Unknown place')}</span>
          </div>` : ''}
          ${category?.name ? `
          <div class="meta-row meta-link" data-category-slug="${slugify(category.name)}" role="link" tabindex="0"
            aria-label="View category ${escapeHtml(category.name)}">
            ${categoryIcon(category)}<span>${escapeHtml(category.name)}</span>
          </div>` : ''}
          ${yoink.personalShelf && who?.username ? `
          <div class="meta-row meta-link" data-personal-owner="${escapeHtml(who.username)}"
            data-personal-slug="${escapeHtml(yoink.personalShelf.slug)}" role="link" tabindex="0"
            aria-label="View personal category ${escapeHtml(yoink.personalShelf.name)}">
            <span aria-hidden="true">${yoink.personalShelf.emoji ? escapeHtml(yoink.personalShelf.emoji) : '🔒'}</span>
            <span>${escapeHtml(yoink.personalShelf.name)} <small class="yoink-personal-note">personal category</small></span>
          </div>` : ''}
        </div>
        ${collectionStrip}
        ${bravoBar}
        ${yoink.social_url && socialLinkLabel(yoink.social_url) ? `
        <a class="v2-btn-more yoink-social" href="${escapeHtml(yoink.social_url)}"
          target="_blank" rel="noopener">${escapeHtml(socialLinkLabel(yoink.social_url))} ↗</a>` : ''}
        ${manageRow(store, yoink, confirming, deleting)}
      </div>
    </article>`;
}
